import { useRef, useState, useEffect, useCallback } from 'react';

const CHAR_FILTER = /[^a-zA-Z0-9 áéíóúÁÉÍÓÚñÑ.,;:()\-\n\r]/g;

/**
 * useSpeechRecognition
 *
 * Statuses:
 *   'idle' | 'listening' | 'transcribing' | 'processing' |
 *   'completed' | 'stopped' | 'timeout' | 'error'
 *
 * Correcciones implementadas:
 *   1. isManuallyStopped — bandera que bloquea cualquier reactivación interna de Chrome.
 *   2. isRunning         — verdadero estado de la sesión (distinto al status visual).
 *   3. silenceTimerRef   — timeout de inactividad configurable (default 60 s).
 *   4. completedTimerRef — el timeout de "completed→listening" se limpia al parar.
 *   5. stoppedTimerRef   — controla el flash "detenido" de 2 s.
 *   6. Prevención de múltiples instancias: abort() antes de start().
 *   7. onstart protegido: si Chrome reinicia solo y isManuallyStopped=true → abort().
 *   8. onend no pisa estados terminales ni estados seteados por stop().
 *
 * @param {object}   options
 * @param {string}   options.lang            BCP-47 (default: 'es-EC')
 * @param {boolean}  options.continuous      Seguir tras cada resultado (default: true)
 * @param {function} options.onFinalResult   Callback(text, confidence)
 * @param {number}   options.silenceTimeout  ms sin voz antes de auto-detener (default: 60000)
 */
export function useSpeechRecognition({
    lang = 'es-EC',
    continuous = true,
    onFinalResult = null,
    silenceTimeout = 60_000,
} = {}) {

    // ── Refs ─────────────────────────────────────────────────────────────────
    const recognitionRef    = useRef(null);
    const onFinalResultRef  = useRef(onFinalResult);

    // Control anti-reactivación: true = el usuario detuvo manualmente
    const isManuallyStopped = useRef(false);

    // true mientras la instancia SpeechRecognition está activa en el browser
    const isRunningRef      = useRef(false);

    const silenceTimerRef   = useRef(null); // temporizador de inactividad
    const completedTimerRef = useRef(null); // flash completed → listening
    const stoppedTimerRef   = useRef(null); // flash stopped → idle

    // Mantener la referencia del callback fresca sin re-crear start()
    useEffect(() => { onFinalResultRef.current = onFinalResult; }, [onFinalResult]);

    // ── State ─────────────────────────────────────────────────────────────────
    const [status, setStatus]                 = useState('idle');
    const [isRunning, setIsRunning]           = useState(false);
    const [interimTranscript, setInterimT]    = useState('');
    const [confidence, setConfidence]         = useState(null);
    const [error, setError]                   = useState('');

    const isSupported =
        typeof window !== 'undefined' &&
        ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

    // ── Limpieza completa al desmontar ────────────────────────────────────────
    useEffect(() => {
        return () => {
            isManuallyStopped.current = true;
            isRunningRef.current = false;
            clearTimeout(silenceTimerRef.current);
            clearTimeout(completedTimerRef.current);
            clearTimeout(stoppedTimerRef.current);
            recognitionRef.current?.abort();
        };
    }, []);

    // ── stop: parada controlada por el usuario ────────────────────────────────
    const stop = useCallback(() => {
        // Marcar como parada manual ANTES de llamar .stop()
        // para que onend/onerror no pisen el estado que seteamos aquí
        isManuallyStopped.current = true;
        isRunningRef.current = false;

        clearTimeout(silenceTimerRef.current);
        clearTimeout(completedTimerRef.current);
        clearTimeout(stoppedTimerRef.current);

        setIsRunning(false);
        setInterimT('');
        setConfidence(null);

        recognitionRef.current?.stop();

        // Mostrar "detenido" durante 2 s, luego volver a idle
        setStatus('stopped');
        stoppedTimerRef.current = setTimeout(() => {
            setStatus(prev => prev === 'stopped' ? 'idle' : prev);
        }, 2000);
    }, []);

    // ── start ─────────────────────────────────────────────────────────────────
    const start = useCallback(() => {
        if (!isSupported) return;

        // Prevenir múltiples instancias: abortar sesión previa si existiera
        if (isRunningRef.current) {
            recognitionRef.current?.abort();
        }

        // Limpiar todos los timers pendientes de sesiones anteriores
        clearTimeout(silenceTimerRef.current);
        clearTimeout(completedTimerRef.current);
        clearTimeout(stoppedTimerRef.current);

        // Reiniciar flags de control
        isManuallyStopped.current = false;
        isRunningRef.current = false;

        setError('');
        setInterimT('');
        setConfidence(null);
        setIsRunning(false);
        setStatus('idle');

        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        const recognition = new SR();
        recognition.lang = lang;
        recognition.continuous = continuous;
        recognition.interimResults = true;
        recognition.maxAlternatives = 1;

        // Helper local: reiniciar el temporizador de inactividad
        // Captura 'silenceTimeout' y 'recognition' vía closure de esta ejecución de start()
        function resetSilenceTimer() {
            clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = setTimeout(() => {
                // Solo actuar si la sesión sigue activa y no fue parada manualmente
                if (!isRunningRef.current || isManuallyStopped.current) return;
                isRunningRef.current = false;
                clearTimeout(completedTimerRef.current);
                // Usar abort() para evitar disparar onerror('aborted')
                recognition.abort();
                setIsRunning(false);
                setInterimT('');
                setStatus('timeout');
                // Volver a idle después de 3 s
                silenceTimerRef.current = setTimeout(() => {
                    setStatus(prev => prev === 'timeout' ? 'idle' : prev);
                }, 3000);
            }, silenceTimeout);
        }

        // ── onstart ──────────────────────────────────────────────────────────
        recognition.onstart = () => {
            // PROTECCIÓN CRÍTICA: Chrome puede disparar onstart de una sesión
            // nueva de forma interna. Si el usuario ya paró, abortamos inmediatamente.
            if (isManuallyStopped.current) {
                recognition.abort();
                return;
            }
            isRunningRef.current = true;
            setIsRunning(true);
            resetSilenceTimer(); // Inicia el contador de inactividad
            setStatus('listening');
        };

        // ── onspeechstart ─────────────────────────────────────────────────────
        recognition.onspeechstart = () => {
            if (isManuallyStopped.current) return;
            resetSilenceTimer(); // Reiniciar al detectar voz
            setStatus('transcribing');
        };

        // ── onspeechend ───────────────────────────────────────────────────────
        recognition.onspeechend = () => {
            if (isManuallyStopped.current) return;
            setStatus(prev => prev === 'transcribing' ? 'processing' : prev);
        };

        // ── onresult ──────────────────────────────────────────────────────────
        recognition.onresult = (e) => {
            if (isManuallyStopped.current) return;

            resetSilenceTimer(); // Cualquier resultado (parcial o final) reinicia el timer

            let interim = '';
            for (let i = e.resultIndex; i < e.results.length; i++) {
                const result = e.results[i];
                if (result.isFinal) {
                    const raw  = result[0].transcript.trim();
                    const clean = raw.replace(CHAR_FILTER, '').toUpperCase();
                    const conf  = result[0].confidence ?? null;
                    if (clean) {
                        onFinalResultRef.current?.(clean, conf);
                        setConfidence(conf);
                        setInterimT('');
                        setStatus('completed');
                        // En modo continuo: volver a 'listening' tras 2 s
                        // Guardamos el timer para poder cancelarlo en stop()
                        clearTimeout(completedTimerRef.current);
                        if (continuous) {
                            completedTimerRef.current = setTimeout(() => {
                                // Solo transicionar si la sesión sigue activa y no fue parada
                                if (!isRunningRef.current || isManuallyStopped.current) return;
                                setStatus(prev => prev === 'completed' ? 'listening' : prev);
                                setConfidence(null);
                            }, 2000);
                        }
                    }
                } else {
                    interim += result[0].transcript;
                }
            }
            if (interim) {
                setInterimT(interim.replace(CHAR_FILTER, '').toUpperCase());
                setStatus(prev =>
                    ['listening', 'processing'].includes(prev) ? 'transcribing' : prev
                );
            }
        };

        // ── onerror ───────────────────────────────────────────────────────────
        recognition.onerror = (e) => {
            clearTimeout(silenceTimerRef.current);
            // 'aborted' = nosotros llamamos abort() — no es un error real
            if (e.error === 'aborted') return;
            // Si fue parada manual, ignorar cualquier error residual
            if (isManuallyStopped.current) return;
            const msgs = {
                'not-allowed':   'Permiso de micrófono denegado. Habilítalo en la configuración del navegador.',
                'no-speech':     'No se detectó voz. Intenta de nuevo.',
                'audio-capture': 'No se encontró micrófono en este dispositivo.',
                'network':       'Error de red al procesar el audio.',
            };
            isRunningRef.current = false;
            setIsRunning(false);
            setError(msgs[e.error] ?? `Error de reconocimiento: ${e.error}`);
            setStatus('error');
            setInterimT('');
        };

        // ── onend ─────────────────────────────────────────────────────────────
        recognition.onend = () => {
            clearTimeout(silenceTimerRef.current);
            isRunningRef.current = false;
            setIsRunning(false);
            setInterimT('');

            // Parada manual: stop() ya seteó el estado → no pisarlo
            if (isManuallyStopped.current) return;

            // Parada involuntaria del browser (límite interno de Chrome, red, etc.)
            // Solo ir a 'idle' si no hay un estado terminal que conservar
            setStatus(prev => {
                if (['completed', 'error', 'stopped', 'timeout'].includes(prev)) return prev;
                return 'idle';
            });
        };

        recognitionRef.current = recognition;
        try {
            recognition.start();
        } catch {
            isRunningRef.current = false;
            setIsRunning(false);
            setError('No se pudo iniciar el reconocimiento de voz.');
            setStatus('error');
        }
    }, [isSupported, lang, continuous, silenceTimeout]);

    return {
        status,
        isRunning,        // true mientras la sesión está activa en el browser
        interimTranscript,
        confidence,
        error,
        isSupported,
        start,
        stop,
    };
}
